import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';
import usePullToRefresh from '../hooks/usePullToRefresh';

const STATUS_COLORS = {
  PENDING: 'text-yellow-400 bg-yellow-400/10',
  ACTIVE: 'text-blue-400 bg-blue-400/10',
  COMPLETED: 'text-green-400 bg-green-400/10',
  CANCELLED: 'text-red-400 bg-red-400/10',
};

const CANCEL_REASONS = [
  'I found a better option',
  'Provider is unresponsive',
  'Schedule conflict',
  'Pricing issue',
  'Other',
];

export default function BookingsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currency } = useCurrencyStore();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [role, setRole] = useState('client');

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOther, setCancelOther] = useState('');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings?role=${role}`);
      setBookings(data);
    } catch {}
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const { pulling, pullDistance } = usePullToRefresh(fetchBookings);

  const filtered = bookings.filter(b => {
    if (activeTab === 'ACTIVE') return ['ACTIVE'].includes(b.status);
    if (activeTab === 'PENDING') return b.status === 'PENDING';
    if (activeTab === 'COMPLETED') return ['COMPLETED', 'CANCELLED'].includes(b.status);
    return true;
  });

  const handleStatusUpdate = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      fetchBookings();
    } catch (err) {
      useToastStore.getState().addToast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelModal) return;
    const reason = cancelReason === 'Other' ? (cancelOther || 'Other') : cancelReason;
    try {
      await api.patch(`/bookings/${cancelModal.id}/status`, { status: 'CANCELLED', cancelReason: reason });
      fetchBookings();
    } catch (err) {
      useToastStore.getState().addToast(err.response?.data?.error || 'Failed', 'error');
    }
    setCancelModal(null);
    setCancelReason('');
    setCancelOther('');
  };

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Pull to refresh indicator */}
      {pullDistance > 0 && (
        <div
          className="flex items-center justify-center overflow-hidden transition-all duration-150"
          style={{ height: Math.min(pullDistance * 0.6, 56) }}
        >
          <div className="flex items-center gap-2">
            {pulling ? (
              <>
                <div className="w-4 h-4 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-brand-purple font-medium">Release to refresh</span>
              </>
            ) : (
              <span className="text-xs text-dark-subtext">Pull to refresh</span>
            )}
          </div>
        </div>
      )}

      <div className="px-4 pt-12 pb-4">
        <h1 className="text-2xl font-bold text-dark-text mb-4" style={{ letterSpacing: '-0.5px' }}>My Orders</h1>

        {/* Role toggle */}
        {['PROVIDER', 'ADMIN'].includes(user?.accountType) && (
          <div className="flex bg-dark-surface border border-dark-border rounded-xl p-1 mb-4">
            <button
              onClick={() => setRole('client')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                role === 'client' ? 'bg-brand-gradient text-white' : 'text-dark-subtext'
              }`}
            >
              As Client
            </button>
            <button
              onClick={() => setRole('provider')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                role === 'provider' ? 'bg-brand-gradient text-white' : 'text-dark-subtext'
              }`}
            >
              As Provider
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-dark-border">
          {['PENDING', 'ACTIVE', 'COMPLETED'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-brand-purple border-b-2 border-brand-purple'
                  : 'text-dark-subtext'
              }`}
            >
              {tab.charAt(0) + tab.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4">
        {loading ? (
          <div className="flex flex-col gap-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-dark-surface rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-dark-text font-semibold mb-1">No {activeTab.toLowerCase()} orders</p>
            <p className="text-dark-subtext text-sm">Your bookings will appear here</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map(b => (
              <div key={b.id} className="bg-dark-surface border border-dark-border rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-dark-muted flex-shrink-0">
                    {b.service?.images?.[0] ? (
                      <img src={b.service.images[0]} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <div className="w-full h-full bg-brand-gradient opacity-50 flex items-center justify-center text-xl">🎨</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-dark-text text-sm font-semibold truncate">{b.service?.title}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_COLORS[b.status]}`}>
                        {b.status.charAt(0) + b.status.slice(1).toLowerCase()}
                      </span>
                    </div>
                    <p className="text-dark-subtext text-xs mt-0.5">
                      {role === 'client'
                        ? (b.provider?.companyName || b.provider?.fullName)
                        : b.client?.fullName}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-brand-purple text-sm font-bold">{formatPrice(b.totalAmount, currency)}</span>
                      <span className="text-dark-subtext text-xs">
                        {new Date(b.startDate).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  {b.status === 'PENDING' && role === 'provider' && (
                    <button
                      onClick={() => handleStatusUpdate(b.id, 'ACTIVE')}
                      className="flex-1 py-2 rounded-xl bg-brand-gradient text-white text-xs font-semibold"
                    >
                      Accept
                    </button>
                  )}
                  {b.status === 'ACTIVE' && role === 'provider' && (
                    <button
                      onClick={() => handleStatusUpdate(b.id, 'COMPLETED')}
                      className="flex-1 py-2 rounded-xl bg-green-600 text-white text-xs font-semibold"
                    >
                      Mark Complete
                    </button>
                  )}
                  {['PENDING', 'ACTIVE'].includes(b.status) && role === 'client' && (
                    <button
                      onClick={() => { setCancelModal(b); setCancelReason(''); setCancelOther(''); }}
                      className="flex-1 py-2 rounded-xl bg-dark-bg border border-red-500/40 text-red-400 text-xs font-semibold"
                    >
                      Cancel
                    </button>
                  )}
                  {b.status === 'COMPLETED' && role === 'client' && !b.review && (
                    <button
                      onClick={() => navigate(`/service/${b.serviceId}?review=1&bookingId=${b.id}`)}
                      className="flex-1 py-2 rounded-xl bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-xs font-semibold"
                    >
                      Leave Review
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel reason modal */}
      {cancelModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <div className="bg-dark-surface border border-dark-border rounded-t-3xl w-full max-w-lg p-6 pb-10">
            <div className="w-10 h-1 bg-dark-border rounded-full mx-auto mb-5" />
            <h2 className="text-lg font-bold text-dark-text mb-1">Cancel Booking</h2>
            <p className="text-dark-subtext text-sm mb-5">Please select a reason for cancelling.</p>

            <div className="flex flex-col gap-2 mb-4">
              {CANCEL_REASONS.map(reason => (
                <button
                  key={reason}
                  onClick={() => setCancelReason(reason)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-colors text-left ${
                    cancelReason === reason
                      ? 'border-brand-purple bg-brand-purple/10 text-dark-text'
                      : 'border-dark-border bg-dark-bg text-dark-subtext'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    cancelReason === reason ? 'border-brand-purple' : 'border-dark-border'
                  }`}>
                    {cancelReason === reason && (
                      <span className="w-2 h-2 rounded-full bg-brand-purple block" />
                    )}
                  </span>
                  {reason}
                </button>
              ))}
            </div>

            {cancelReason === 'Other' && (
              <textarea
                value={cancelOther}
                onChange={e => setCancelOther(e.target.value)}
                placeholder="Please describe your reason..."
                rows={3}
                className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple resize-none mb-4"
              />
            )}

            <div className="flex gap-3">
              <button
                onClick={handleConfirmCancel}
                disabled={!cancelReason}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-50"
              >
                Confirm Cancellation
              </button>
              <button
                onClick={() => { setCancelModal(null); setCancelReason(''); setCancelOther(''); }}
                className="flex-1 py-3 rounded-xl bg-dark-bg border border-dark-border text-dark-subtext font-medium text-sm"
              >
                Keep Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
